// Workforce › Outbox (`/c/{slug}/agents/outbox`; STEP1-OUTBOX, build order increment 1 B): every
// outward act an agent takes after its approval — pending, sent, failed and dead letters — with
// Retry (a failed item or a dead letter) and Cancel (anything not gone out) as hover icons.
// Managers see the company's; anyone else the items they or their crew asked for, or decided.
// Calls on load: `outbox.list` (one page, the status filter as `?status`).
import type { OperationOutput } from "@companyos/sdk";
import { Badge, Button, EmptyState, InfoTip, PageHeader, ViewTabs } from "@companyos/ui";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { BanIcon, RotateCcwIcon, SendIcon } from "lucide-react";
import type { Api } from "../../api/client";
import type { CompanyAccess } from "../company";
import { problemText } from "../de/de-status";
import { formatDateTime } from "../format";
import { LoadError, Loading } from "../screen-parts";
import { RowAction, RowActions } from "../settings/row-actions";

export type OutboxItem = OperationOutput<"outbox.list">["data"][number];
export const OUTBOX_TABS = ["all", "pending", "sent", "failed"] as const;
export type OutboxTab = (typeof OUTBOX_TABS)[number];

const TAB_LABELS: Record<OutboxTab, string> = {
  all: "All",
  pending: "Pending",
  sent: "Sent",
  failed: "Failed",
};

const STATUS: Record<
  OutboxItem["status"],
  [string, "neutral" | "info" | "success" | "warning" | "danger"]
> = {
  queued: ["Queued", "neutral"],
  sending: ["Sending", "info"],
  sent: ["Sent", "success"],
  failed: ["Retrying", "warning"],
  dead: ["Dead letter", "danger"],
  cancelled: ["Cancelled", "neutral"],
};

const ACTION_LABELS: Record<OutboxItem["action"], string> = {
  "mail.send": "Mail",
  "crm.write": "CRM write",
  "message.send": "Message",
  "api.request": "API call",
};

export const outboxKey = (co: string) => ["company", co, "outbox"] as const;

/** One line naming what the act does (recipients, object, path), never its body. */
export function outboxSummary(x: Pick<OutboxItem, "action" | "payload">): string {
  const r = ((x.payload as { request?: unknown }).request ?? {}) as Record<string, unknown>;
  const list = (v: unknown) => (Array.isArray(v) ? v.filter((s) => typeof s === "string") : []);
  switch (x.action) {
    case "mail.send": {
      const to = list(r.to);
      const subject = typeof r.subject === "string" ? r.subject : "";
      return `${to.slice(0, 2).join(", ")}${to.length > 2 ? ` +${to.length - 2}` : ""}${subject ? ` · ${subject}` : ""}`;
    }
    case "crm.write":
      return `${String(r.op ?? "")} ${String(r.object ?? "")}`.trim();
    case "message.send":
      return String(r.to ?? "");
    case "api.request":
      return `${String(r.method ?? "")} ${String(r.path ?? "")}`.trim();
  }
}

export function OutboxScreen({
  api,
  company,
  tab,
  onTab,
  item,
}: {
  api: Api;
  company: CompanyAccess;
  tab: OutboxTab;
  onTab: (t: OutboxTab) => void;
  /** The row an inbox notice points at: highlighted and scrolled into view. */
  item?: string;
}) {
  const co = company.id;
  const qc = useQueryClient();
  // The tab is filtered by the server and paged ("Load more"), so an old dead letter stays reachable
  const q = useInfiniteQuery({
    queryKey: [...outboxKey(co), tab],
    queryFn: ({ pageParam }) =>
      api.client.outbox.list({
        params: { co },
        query: {
          limit: 50,
          ...(tab === "all" ? {} : { group: tab }),
          ...(pageParam ? { cursor: pageParam } : {}),
        },
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next_cursor,
  });
  const retry = useMutation({
    mutationFn: (id: string) => api.client.outbox.retry({ params: { co, id } }),
    onSettled: () => qc.invalidateQueries({ queryKey: outboxKey(co) }),
  });
  const cancel = useMutation({
    mutationFn: (id: string) => api.client.outbox.cancel({ params: { co, id }, body: {} }),
    onSettled: () => qc.invalidateQueries({ queryKey: outboxKey(co) }),
  });
  // The row an inbox notice points at, fetched on its own: shown first whatever its page or status
  const target = useQuery({
    queryKey: [...outboxKey(co), "item", item],
    enabled: !!item,
    queryFn: () =>
      api.client.outbox.list({ params: { co }, query: { item: item as string, limit: 1 } }),
  });
  const listed = (q.data?.pages ?? []).flatMap((p) => p.data);
  const pinned = target.data?.data[0];
  const rows = pinned ? [pinned, ...listed.filter((x) => x.id !== pinned.id)] : listed;
  const gone = !!item && target.isSuccess && !pinned;
  const failure = retry.error ?? cancel.error;
  return (
    <>
      <PageHeader
        spacing="tabs"
        eyebrow="Team"
        title="Outbox"
        badge={
          <InfoTip label="About the Outbox">
            Approved outward acts (mail, CRM writes, messages, API calls) wait here until they go
            out. A dead letter was refused or ran out of attempts; if its answer was lost it may
            have gone out, so check before you retry. Without a connection an act is a dry run:
            logged, nothing sent.
          </InfoTip>
        }
      />
      <ViewTabs
        aria-label="Outbox"
        value={tab}
        onValueChange={(v) => onTab(v as OutboxTab)}
        tabs={OUTBOX_TABS.map((t) => ({ value: t, label: TAB_LABELS[t] }))}
      />
      <div className="flex min-w-0 flex-col gap-3 pt-5">
        {failure ? (
          <p role="alert" className="text-sm text-destructive">
            {problemText(failure, "That did not work. Try again.")}
          </p>
        ) : null}
        {gone ? (
          <p className="text-sm text-muted-foreground">That item is no longer in your outbox.</p>
        ) : null}
        {item && target.isError ? (
          <p role="alert" className="text-sm text-destructive">
            Could not load the item this link points at.{" "}
            <Button variant="link" size="sm" onClick={() => void target.refetch()}>
              Try again
            </Button>
          </p>
        ) : null}
        {q.isPending || (!!item && target.isPending) ? (
          <Loading label="Loading the outbox" rows={4} />
        ) : q.isError ? (
          <LoadError what="Outbox" error={q.error} />
        ) : !rows.length ? (
          <EmptyState
            variant="card"
            icon={SendIcon}
            title={
              tab === "all" ? "Nothing in the outbox" : `Nothing ${TAB_LABELS[tab].toLowerCase()}`
            }
            description="Approved outward acts appear here."
          />
        ) : (
          <ul aria-label="Outbox items" className="flex flex-col" data-slot="outbox-list">
            {rows.map((x) => {
              const [label, tone] = STATUS[x.status];
              const dryRun = x.provider === "log_only";
              const canRetry = x.status === "dead" || x.status === "failed";
              const canCancel =
                x.status === "queued" || x.status === "failed" || x.status === "dead";
              const summary = outboxSummary(x);
              // A failed item's error, or why a queued one waits (its sender, the day's limit)
              const why =
                x.dead_reason ??
                (x.status === "failed" || x.status === "queued" ? x.last_error : null);
              return (
                <li
                  key={x.id}
                  data-status={x.status}
                  data-selected={x.id === item ? "" : undefined}
                  aria-current={x.id === item ? "true" : undefined}
                  ref={
                    x.id === item ? (el) => el?.scrollIntoView?.({ block: "nearest" }) : undefined
                  }
                  className="group/row flex min-h-10 items-center gap-3 border-b border-border px-1 py-2 text-[13px] data-[selected]:bg-accent"
                >
                  <Badge variant={tone}>{label}</Badge>
                  <span className="w-20 shrink-0 text-muted-foreground">
                    {ACTION_LABELS[x.action]}
                  </span>
                  <span className="min-w-0 flex-1 truncate" title={summary}>
                    {summary || "—"}
                  </span>
                  {dryRun ? <Badge variant="neutral">Dry run</Badge> : null}
                  {why ? <InfoTip label={`Why: ${summary}`}>{why}</InfoTip> : null}
                  <span className="shrink-0 tabular-nums text-muted-foreground">
                    {formatDateTime(x.sent_at ?? x.send_at ?? x.created_at)}
                  </span>
                  <RowActions className="w-16">
                    {canRetry ? (
                      <RowAction
                        icon={RotateCcwIcon}
                        label={`Retry ${summary}`}
                        tip="Retry"
                        disabled={retry.isPending}
                        onClick={() => retry.mutate(x.id)}
                      />
                    ) : null}
                    {canCancel ? (
                      <RowAction
                        icon={BanIcon}
                        label={`Cancel ${summary}`}
                        tip="Cancel"
                        disabled={cancel.isPending}
                        onClick={() => cancel.mutate(x.id)}
                      />
                    ) : null}
                  </RowActions>
                </li>
              );
            })}
          </ul>
        )}
        {q.hasNextPage ? (
          <Button
            variant="ghost"
            size="sm"
            className="self-start"
            disabled={q.isFetchingNextPage}
            onClick={() => void q.fetchNextPage()}
          >
            Load more
          </Button>
        ) : null}
      </div>
    </>
  );
}
